import logger from "@/lib/logger";
import { prefersReducedMotion } from "@/main/util/motionPreference";

/**
 * The route-transition seam (`MAF-3`).
 *
 * <p>Wraps the DOM-replacing step of a SOFT navigation in a same-document View Transition, so screens
 * cross-fade instead of blinking. That "blink" is the canonical *this is a website in a WebView* tell,
 * and it is the largest single gap in the FEEL dimensions
 * (`designs/08-audits-and-process/ux-critique/mobile-app-feel.md`, `MAF-3`).
 *
 * <p><b>Three things this module deliberately does NOT do.</b>
 *
 * <p><b>1. It does not wrap the awaits.</b> `startViewTransition` freezes the page from the moment it
 * captures the outgoing frame until the callback has updated the DOM. Wrapping
 * `await fetchHtmlDocument(...)` would freeze it for a whole network round-trip and produce an app that
 * feels *slower* than the blink it replaced. Callers pass the synchronous swap only.
 *
 * <p><b>2. It does not swallow the navigation's errors.</b> `runEnhancedNavigation`'s catch ladder
 * distinguishes `AbortError`, `FrontendReleaseHardReloadRequiredError` and
 * `CrossLocaleNavigationUnsupportedError` from the generic hard-navigation fallback. An error that did
 * not arrive would disable all of those branches silently, so whatever the update threw is re-thrown
 * unchanged. The transition's OWN promises are a different matter and are ignored: an interrupted
 * same-document transition rejects `finished` in ordinary use, the DOM update has already happened by
 * then, and an unattached rejection would be a console error on every interrupted navigation.
 *
 * <p><b>3. It does not write or traverse history</b> (rule 33) and holds no store (rule 31). It is a
 * function over a callback; every history write in the wrapped region is still the caller's existing
 * `pushPageHistoryEntry` / `replacePageHistoryEntry` call.
 *
 * <p><b>Degrading is the COMMON path, not the edge case.</b> `document.startViewTransition` is absent
 * in Firefox, in Safari before 18, in older Android WebViews, and in jsdom — which is where every unit
 * test in this package runs. ⚠️ TypeScript's `lib.dom` nevertheless declares it as a REQUIRED member of
 * `Document`, so the type says it is always there and the runtime disagrees; every read of it here goes
 * through a runtime check for that reason.
 *
 * <p>Motion vocabulary lives in `css/01-base.css` beside every other transition in the product: one 200 ms
 * cross-fade on `--motion-ease-out-strong`, shortened to 120 ms under `prefers-reduced-motion` the way the rest of
 * that file shortens rather than removes. The JS gate below goes further and skips the transition outright, because
 * the UA's own default cross-fade (250 ms) would otherwise still play in any engine where our stylesheet had not applied.
 *
 * <p>Design: `designs/01-now/2026-09-01-route-transition-seam-and-maf-7-remainder-design.md` §2.
 *
 * <p><b>No direction, by decision.</b> U38 (r156, 2026-10-20) passed a push/back/tab transition type here so the CSS
 * could slide the page; the operator reversed it 2026-10-23 (the screen shook for no reason). The transition is the
 * plain callback form again. ui-fundamentals §7c U38.
 */

type StartViewTransition = (update: () => void) => ViewTransition | undefined;

function resolveStartViewTransition(): StartViewTransition | null {
    if (typeof document === "undefined") {
        return null;
    }

    // Read through `Partial<Document>`: the lib type is not optional, the runtime member is.
    const api = (document as Partial<Document>).startViewTransition;
    if (typeof api !== "function") {
        return null;
    }

    return api.bind(document) as unknown as StartViewTransition;
}

/** A transition promise is decorative. Attach a sink so an interrupted one is not an unhandled rejection. */
function ignoreRejection(value: unknown): void {
    if (value && typeof (value as Promise<unknown>).then === "function") {
        void (value as Promise<unknown>).catch(() => {});
    }
}

/**
 * Runs `applyDomUpdate` inside a View Transition where the engine has one, and plainly where it does
 * not. Resolves with whatever the update returned; rejects with whatever it threw.
 *
 * <p>The update always runs exactly once. In the unsupported and reduced-motion paths it runs
 * SYNCHRONOUSLY at call time, so nothing can interleave between the caller's call and the DOM swap
 * that could not interleave before this wrapper existed.
 */
export async function runNavigationViewTransition<T>(applyDomUpdate: () => T): Promise<T> {
    const startViewTransition = resolveStartViewTransition();
    if (!startViewTransition || prefersReducedMotion()) {
        return applyDomUpdate();
    }

    let result: T | undefined;
    let thrownError: unknown;
    let didThrow = false;
    let didRun = false;

    const runUpdateOnce = (): void => {
        // Guards the recovery below against a late engine invocation running the swap a second time.
        if (didRun) {
            return;
        }

        didRun = true;
        try {
            result = applyDomUpdate();
        } catch (error) {
            didThrow = true;
            thrownError = error;
        }
    };

    let transition: ViewTransition | undefined;
    try {
        transition = startViewTransition(runUpdateOnce);
    } catch (error) {
        // The engine declined the transition. A broken animation must never cost the member the
        // navigation, so fall through to the plain swap.
        logger.info("[PageViewTransition] startViewTransition threw; applying the update directly.", error);
        runUpdateOnce();
    }

    if (transition) {
        ignoreRejection(transition.ready);
        ignoreRejection(transition.finished);

        try {
            // Resolves once the DOM has been updated — NOT once the animation has finished. The caller's
            // post-mount work must not wait on 180 ms of cross-fade.
            await transition.updateCallbackDone;
        } catch {
            // Cannot happen: `runUpdateOnce` never throws. Kept so a non-conforming implementation
            // cannot turn a cosmetic failure into a failed navigation.
        }
    }

    if (!didRun) {
        // Belt and braces for an engine whose returned object we could not await. Never running the
        // update would leave the member on the old page with the address bar already changed, which is
        // strictly worse than any missing animation.
        logger.warn("[PageViewTransition] The view transition never ran the update; applying it directly.");
        runUpdateOnce();
    }

    if (didThrow) {
        throw thrownError;
    }

    return result as T;
}
